import { Link } from "@tanstack/solid-router";
import { For, Show } from "solid-js";

import type { ActivityKind, ActivityRead, AttachableEntity } from "~/lib/types";

type Props = {
  wsKey: () => string;
  items: () => ActivityRead[];
};

const KIND_COLOR: Record<ActivityKind, string> = {
  meeting: "badge-success",
  call: "badge-warning",
  email: "badge-info",
  task: "badge-secondary",
  system_event: "badge-neutral",
};

function attachedToLink(
  wsKey: string,
  entityType: AttachableEntity,
  entityId: string,
):
  | { to: "/workspaces/$wsKey/accounts/$accId"; params: { wsKey: string; accId: string } }
  | { to: "/workspaces/$wsKey/contacts/$conId"; params: { wsKey: string; conId: string } }
  | { to: "/workspaces/$wsKey/deals/$dealId"; params: { wsKey: string; dealId: string } }
  | null {
  switch (entityType) {
    case "account":
      return {
        to: "/workspaces/$wsKey/accounts/$accId",
        params: { wsKey, accId: entityId },
      };
    case "contact":
      return {
        to: "/workspaces/$wsKey/contacts/$conId",
        params: { wsKey, conId: entityId },
      };
    case "deal":
      return {
        to: "/workspaces/$wsKey/deals/$dealId",
        params: { wsKey, dealId: entityId },
      };
    default:
      return null;
  }
}

// Read-only timeline list, used on the workspace home page. The entity-scoped
// EntityTimeline component handles the composer/edit cases.
export function EntityTimelineFeed(props: Props) {
  return (
    <ul class="mt-2 flex flex-col gap-3">
      <For each={props.items()}>
        {(a) => {
          const link = attachedToLink(
            props.wsKey(),
            a.primary_entity_type,
            a.primary_entity_id,
          );
          return (
            <li class="flex gap-3 border-l-4 border-success pl-3 py-2">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 text-xs opacity-60 flex-wrap">
                  <span class={`badge ${KIND_COLOR[a.kind]} badge-sm`}>{a.kind}</span>
                  <Show when={a.direction}>
                    <span class="badge badge-outline badge-sm">{a.direction}</span>
                  </Show>
                  <Show when={a.status !== "completed"}>
                    <span class="badge badge-outline badge-sm">{a.status}</span>
                  </Show>
                  <span>{new Date(a.started_at).toLocaleString()}</span>
                  <Show when={link}>
                    {(l) => (
                      <Link {...l()} class="link link-primary">
                        {a.primary_entity_type}
                      </Link>
                    )}
                  </Show>
                </div>
                <p class="mt-1 font-medium truncate">{a.subject}</p>
                <Show when={a.body}>
                  <p class="text-sm opacity-80 whitespace-pre-wrap mt-1 line-clamp-2">
                    {a.body}
                  </p>
                </Show>
              </div>
            </li>
          );
        }}
      </For>
    </ul>
  );
}
